import { useEffect, useMemo, useState } from 'react';

import { Stack } from '@mui/material';

import {
    setPreHeader,
    setSenderProfileId,
    setSendChannel,
    setSmsContent,
    setSubjectLine,
    setTemplateId,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignChannelLabels, CampaignChannels } from 'domains/campaigns/types';
import { SMS, Template } from 'domains/content/types';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import AutocompleteContent from 'domains/core/components/AutocompleteContent';
import { ContentItem } from 'domains/core/components/AutocompleteContent/AutocompleteContent';
import FormLayout from 'domains/core/components/FormLayout';
import Select from 'domains/core/components/Select';
import useSmses from 'hooks/queries/useSmses';
import useTemplates from 'hooks/queries/useTemplates';

type Props = {
    activeSendIndex: number;
    dispatchCampaignAction: React.Dispatch<any>;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
};

type MixedContentChannel = CampaignChannels.EMAIL | CampaignChannels.SMS;

/**
 * Entry point for every send in a mixed-channel campaign: the user picks a piece of content —
 * not a channel — from a list for the selected channel, and the send's channel is derived from
 * whichever list the picked item came from. Once a channel is set, CustomCampaignContentStep
 * routes to that channel's normal content step for the rest of the fields (sender profile, etc).
 */
const hasAutoResponses = (sms: SMS): boolean => {
    if ((sms.autoResponses?.length ?? 0) > 0) return true;
    if (sms.autoResponsesMl) return Object.values(sms.autoResponsesMl).some((arr) => arr.length > 0);
    return false;
};

const CustomCampaignChooseSendContentStep = ({
    activeSendIndex,
    dispatchCampaignAction,
    setIsContinueEnabled,
}: Props) => {
    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates();
    const { data: allSmses = [], isLoading: isLoadingSmses } = useSmses();

    // Exclude SMS templates that have auto-responses configured — auto-response attribution
    // is based on the most-recent send and breaks when multiple SMS sends exist in one curation.
    const smses = useMemo(() => allSmses.filter((sms) => !hasAutoResponses(sms)), [allSmses]);

    const channelOptions: { label: string; value: MixedContentChannel }[] = [
        { label: CampaignChannelLabels[CampaignChannels.EMAIL], value: CampaignChannels.EMAIL },
        { label: CampaignChannelLabels[CampaignChannels.SMS], value: CampaignChannels.SMS },
    ];

    const [searchInput, setSearchInput] = useState('');
    const [channelFilter, setChannelFilter] = useState<MixedContentChannel>(CampaignChannels.EMAIL);

    // Picking content immediately hands the send off to its channel-specific step (see
    // CustomCampaignContentStep), so this step never itself has a "complete" state to enable.
    useEffect(() => {
        setIsContinueEnabled(false);
    }, [setIsContinueEnabled]);

    const options = useMemo<AutocompleteOption<ContentItem>[]>(() => {
        const searchTerm = searchInput.trim().toLowerCase();
        const pools: Record<MixedContentChannel, ContentItem[]> = {
            [CampaignChannels.EMAIL]: templates,
            [CampaignChannels.SMS]: smses,
        };
        const searchable = pools[channelFilter];
        const merged = searchTerm
            ? searchable.filter((item) => item.name.toLowerCase().includes(searchTerm))
            : searchable;

        // AutocompleteContent treats index 0 as a reserved "Create New" slot (see its
        // isCreateOption check), so real items must start at index 1 or the first real match
        // gets swallowed by that placeholder instead of being selectable.
        return merged.map((item, position) => ({
            index: position + 1,
            label: item.name,
            value: item,
        }));
    }, [templates, smses, searchInput, channelFilter]);

    const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
        setSearchInput(value);
    };

    const handleChannelFilterChange = (event: any) => {
        setChannelFilter(event.target.value);
    };

    const handleChange = (_event: React.SyntheticEvent, option: AutocompleteOption<ContentItem> | null) => {
        const item = option?.value;
        if (!item) return;

        if (templates.includes(item as Template)) {
            const template = item as Template;
            dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.EMAIL));
            dispatchCampaignAction(setTemplateId(template.id, activeSendIndex));
            dispatchCampaignAction(setSenderProfileId(template.senderProfileId ?? null, activeSendIndex));
            dispatchCampaignAction(setSubjectLine(template.subjectLine ?? '', activeSendIndex));
            dispatchCampaignAction(setPreHeader(template.preHeader ?? '', activeSendIndex));
            return;
        }

        dispatchCampaignAction(setSendChannel(activeSendIndex, CampaignChannels.SMS));
        dispatchCampaignAction(setSmsContent({ contentId: item.id, contentName: item.name }, activeSendIndex));
    };

    return (
        <FormLayout
            header="select content"
            stepName="content"
            guidingText="Select the channel, then choose content — the send's channel is set automatically based on what you pick."
        >
            <Stack spacing={2} width="100%">
                <Select
                    data-testid="mixed-content-channel-filter"
                    label="Channel"
                    sx={{ textTransform: 'capitalize', width: '12rem' }}
                    value={channelFilter}
                    values={channelOptions}
                    onChange={handleChannelFilterChange}
                />
                <AutocompleteContent
                    options={options}
                    value={null}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    textfieldInput={searchInput}
                    isLoading={isLoadingTemplates || isLoadingSmses}
                />
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignChooseSendContentStep;
